import { Field, Form, useFormikContext } from 'formik'
import isEqual from 'lodash-es/isEqual'
import React from 'react'
import { useLocation } from 'react-router-dom'

import { Dashboard, PageLayout } from '@components/common'
import {
  DashboardBackButton,
  FeatureFlagDecorator,
  useDashboardFeatureFlags,
  useDashboardPermissions,
} from '@components/utils'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  Fieldset,
  HeadContent,
  Loading,
  SaveShortcutListener,
  Select,
  Subsection,
} from '@fourthwall/components'
import { Offer } from '@fourthwall/services/dist/models/offer'
import { useFocusFirstErrorOnSubmit } from '@fourthwall/utils/dist/formik'
import { routing } from '@utils/routing'
import { addProtocol } from '@utils/url'

import { ConfirmBar } from '../../modules/ConfirmBar'
import { LABEL_BY_FULFILLMENT_SERVICE, LABEL_BY_MANUFACTURING_SERVICE } from '../Product.consts'
import { getFulfillmentType } from '../Product.utils'
import { ProductFiles } from '../ProductFiles/ProductFiles'
import { ProductBanners } from './ProductBanners'
import { ProductBespokeProductId } from './ProductBespokeProductId/ProductBespokeProductId'
import { ProductOrdersContainer } from './ProductOrders'
import { ProductPricing } from './ProductPricing'
import type { ProductFormState, ProductViewProps } from './SingleProduct.types'
import { ProductDetails, ProductToolbar } from './components'
import { DesignEditButton } from './modules/designEditButton/DesignEditButton'
import { DownloadArtFilesButton } from './modules/downloadArtFilesButton/DownloadArtFilesButton'
import { Shipping } from './modules/shipping/Shipping'
import { Variants } from './modules/variants'

import FULFILLING_SERVICES = Offer.FULFILLING_SERVICES
import MANUFACTURING_SERVICES = Offer.MANUFACTURING_SERVICES

export const SingleProductView = ({
  productId,
  productCustomizationId,
  productIsDeleting,
  productUpdateIsSubmitting,
  offerImages,
  images,
  offerType,
  productBanners,
  rightContent,
  name,
  status,
  slug,
  productIsFetching,
  shippingFlatRatesAreFetching,
  productStatusIsUpdating,
  productStockIsSubmitting,
  isUploadingImages,
  shopBaseUri,
  maxDigitalFilesSize,
  isTotalDigitalFilesSizeExceeded,
  isFreeProduct,
  pricingType,
  showPricingPerVariant,
  togglePricingType,
  applyToAllVariants,
  onChangeUrl,
  onOrderSamples,
  onUnarchive,
}: ProductViewProps) => {
  const location = useLocation()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { hasPermission } = useDashboardPermissions()

  const { values, initialValues, isSubmitting, isValid, submitForm, resetForm } =
    useFormikContext<ProductFormState>()

  useFocusFirstErrorOnSubmit()

  const isUploadingFiles = values.digitalItems.some((item) => item.isUploading)
  const fulfillmentType = getFulfillmentType(values.fulfillmentService)
  const isFormEditable = hasPermission('products.edit.details')

  const productUrl = `${shopBaseUri}/products/${slug}`
  const isFormDirty = !isEqual(initialValues, values)

  const renderOfferImages = () => {
    if (!offerImages) return null

    return (
      <Subsection title={fulfillmentType === 'DIGITAL' ? 'Photography' : 'Photography and design'}>
        {offerImages}
        {productCustomizationId && isFormEditable && (
          <div style={{ marginTop: 16 }}>
            <DesignEditButton offerId={productId} customizationId={productCustomizationId} />
            <span style={{ marginLeft: 16 }}>
              <DownloadArtFilesButton customizationId={productCustomizationId} />
            </span>
          </div>
        )}
      </Subsection>
    )
  }

  const renderBottomContent = () => {
    if (!images) return null

    return (
      <>
        <Variants applyToAllVariants={applyToAllVariants} />
        <ProductOrdersContainer offerId={productId} />
      </>
    )
  }

  const renderLeftContent = () => {
    const shouldRenderFiles = () => {
      switch (fulfillmentType) {
        case 'ON_DEMAND':
        case 'SCREEN_PRINTED':
        case 'FULFILLED_BY_CREATOR':
          return false
        case 'DIGITAL':
          return true
      }
    }

    const renderPricing = () => {
      if (showPricingPerVariant) {
        return null
      }

      return (
        <ProductPricing
          pricingType={pricingType}
          togglePricingType={togglePricingType}
          applyToAllVariants={applyToAllVariants}
        />
      )
    }

    const renderManufacturer = () => {
      const options = MANUFACTURING_SERVICES.filter((value) => value !== 'DIGITAL_ITEM').map(
        (value) => ({ label: LABEL_BY_MANUFACTURING_SERVICE[value], value }),
      )

      return (
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Select}
              name="manufacturingService"
              label="Manufacturer"
              options={options}
              disabled={!isFormEditable}
            />
            {values.manufacturingService === 'FOURTHWALL_BESPOKE' && (
              <ProductBespokeProductId bespokeProductId={values.bespokeProductId} />
            )}
          </Fieldset.Item>
        </Fieldset.Row>
      )
    }

    const renderFulfiller = () => {
      const options = FULFILLING_SERVICES.filter((value) => value !== 'DIGITAL_ITEM')
        .map((value) => ({ label: LABEL_BY_FULFILLMENT_SERVICE[value], value }))
        .sort((a, b) => a.label.localeCompare(b.label))

      return (
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              component={Select}
              name="fulfillmentService"
              label="Fulfiller"
              options={options}
              disabled={!isFormEditable}
            />
          </Fieldset.Item>
        </Fieldset.Row>
      )
    }

    return (
      <>
        <ProductBanners {...productBanners} />
        <ProductDetails
          productId={productId}
          isFormEditable={isFormEditable}
          showCategories={['DIGITAL', 'MANUAL'].includes(offerType)}
        />
        <Subsection>{renderPricing()}</Subsection>
        {shouldRenderFiles() && (
          <Subsection title="Files delivered on purchase">
            <ProductFiles
              maxSize={maxDigitalFilesSize}
              isTotalDigitalFilesSizeExceeded={isTotalDigitalFilesSizeExceeded}
              isFreeProduct={isFreeProduct}
              withMembersOnly={values.withMembersOnly}
              offerId={productId}
              files={values.digitalItems}
              isFormEditable={isFormEditable}
            />
          </Subsection>
        )}
        <FeatureFlagDecorator
          {...dashboardFeatureFlags.productAdvancedOptions}
          wrapper={Subsection}
        >
          <Subsection title="Manufacturer and Fulfiller">
            <Fieldset>
              {renderManufacturer()}
              {renderFulfiller()}
            </Fieldset>
          </Subsection>
        </FeatureFlagDecorator>
        <Shipping />
        {renderOfferImages()}
      </>
    )
  }

  return (
    <>
      <Loading
        isLoading={
          isSubmitting ||
          productIsFetching ||
          shippingFlatRatesAreFetching ||
          productUpdateIsSubmitting ||
          productIsDeleting ||
          productStatusIsUpdating ||
          productStockIsSubmitting
        }
      >
        <HeadContent title={name} />
        <Form>
          <PageLayout
            leftContent={renderLeftContent()}
            rightContent={rightContent}
            bottomContent={renderBottomContent()}
          >
            <Dashboard.ItemViewHeader
              name={name}
              link={{
                url: addProtocol(productUrl),
                label: addProtocol(productUrl),
                copyUrl: addProtocol(productUrl),
              }}
              status={status}
              toolbar={
                <ProductToolbar
                  onChangeUrl={onChangeUrl}
                  onOrderSamples={onOrderSamples}
                  onUnarchive={onUnarchive}
                />
              }
              backButton={
                <DashboardBackButton
                  label="Products"
                  to={{
                    pathname: routing.products.all.self,
                    search: location.search,
                  }}
                />
              }
            />
          </PageLayout>
        </Form>
      </Loading>
      <SaveShortcutListener disabled={!isFormDirty} onSave={submitForm} />
      <ConfirmBar
        isOpen={isFormDirty || isUploadingImages || productUpdateIsSubmitting}
        isLoading={
          isUploadingFiles || isUploadingImages || productUpdateIsSubmitting || isSubmitting
        }
        onConfirm={submitForm}
        onCancel={() => resetForm({ values: initialValues })}
      />
      <LeavingGuard
        when={(isFormDirty && !!productId) || isUploadingImages}
        onSave={submitForm}
        isValid={isValid}
      />
    </>
  )
}
